import { useMemo, useState } from 'react'
import Layout from 'shared/layouts/containers/Main'
import useInit from './hooks/useInit'
import DeleteModal from 'shared/components/DeleteModal'
import { apiUrls, roles, settings } from 'consts'
import getRows from './utils/getRows'
import getGridColumns from './utils/getGridColumns'
import {
  PagingState,
  SearchState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  SortingState,
  DataTypeProvider,
  Column,
  Sorting,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
  PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4'
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid/dist/dx-react-grid'
import DevicesFilter from './filter'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import hasAnyRole from 'shared/utils/hasAnyRole'
import getBrandContent from 'utils/getBrandContent'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import Footer from 'shared/components/Footer'
import { IonIcon } from '@ionic/react'
import { cloudUpload, filter as filterIcon } from 'ionicons/icons'
import { formatDate, stringToDate } from 'utils/dateUtils'
import compareDates from './utils/compareDates'
import naturalSort from './utils/naturalSort'
import { Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core'
import showCustomerSwitcher from 'utils/showCustomerSwitcher'
import { NavLink } from 'react-router-dom'
import getPageSize from 'utils/getPageSize'
import CreateStatusBadgeFormatter from 'components/CreateStatusBadge'

const Devices: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [rows, setRows] = useState<any>([])
  const [columns, setColumns] = useState<Array<Column>>([])
  const [columnWidths, setColumnWidths] = useState<Array<TableColumnWidthInfo>>([])
  const [sorting, setSorting] = useState<Array<Sorting>>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [filter, setFilter] = useState<object | null>(null)
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)
  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
  const [filterVisible, setFilterVisible] = useState<boolean>(false)

  const deviceCount: number = useMemo(() => rows.length, [rows])

  const firstColumnName = useMemo(
    () => columns.find((c) => c.name !== 'action')?.name ?? '',
    [columns],
  )

  useInit({
    getRows,
    getGridColumns,
    setRows,
    setDeleteEntryId,
    setShowModal,
    setShowOverlay,
    setColumns,
    setColumnWidths,
    setSorting,
    filter,
  })

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className='small with-action min-mobile-width' />
  )

  const TableRowComponent = ({ row, ...restProps }: any) => (
    <Table.Row
      {...restProps}
      row={row}
      style={row?.is_replacement ? { opacity: 0.7, fontStyle: 'italic' } : undefined}
    />
  )

  const isAdmin: boolean = hasAnyRole([roles.admin, roles.superAdmin])
  const isSuperAdmin: boolean = hasAnyRole([roles.superAdmin])

  const [dateFormatter] = useState<any>({
    for: ['last_used_date', 'warranty_expiration_date'],
    formatterComponent: (props) => formatDate(stringToDate(props.value)),
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const [integratedSortingColumnExtensions] = useState([
    { columnName: 'last_used_date', compare: compareDates },
    { columnName: 'warranty_expiration_date', compare: compareDates },
    { columnName: 'label', compare: naturalSort },
    { columnName: 'serial_number', compare: naturalSort },
  ])

  const SearchPanelInsert = (
    <>
      <a className='btn btn-light inline-space-r' onClick={() => setFilterVisible(!filterVisible)}>
        <IonIcon icon={filterIcon} className='ionicon' style={{ fontSize: '12px' }} />
        {!filterVisible ? 'Show Filters' : 'Hide Filters'}
      </a>
      {isSuperAdmin && (
        <NavLink to='/import-devices' className='btn btn-primary me-3'>
          <IonIcon
            icon={cloudUpload}
            className='ionicon'
            style={{ fontSize: '16px', marginBottom: '-3px' }}
          />
          Import Devices
        </NavLink>
      )}
    </>
  )

  const SearchPanelInputComponent = (props) => {
    return SearchPanelInput({
      insert: SearchPanelInsert,
      addUrl: isAdmin ? '/add-device' : undefined,
      addText: isAdmin ? 'Add Device' : undefined,
      inputProps: props,
    })
  }

  const PagingContainer = (props) => <PagingPanel.Container {...props} className='table-footer' />

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showMinimizedBarButton={true}
      showOverlay={showOverlay}
      headerInsert={
        showCustomerSwitcher() ? (
          <HeaderCustomerSelect
            setShowOverlay={setShowOverlay}
            handleCustomerChanged={(customer) => setCurrentCustomer(customer)}
          />
        ) : undefined
      }
    >
      <>
        <DevicesFilter
          setShowOverlay={setShowOverlay}
          handleFilter={(filter) => setFilter(filter)}
          currentCustomer={currentCustomer}
          filterVisible={filterVisible}
        />

        <Grid rows={rows} columns={columns}>
          <DataTypeProvider {...dateFormatter} />
          {firstColumnName && (
            <DataTypeProvider
              for={[firstColumnName]}
              formatterComponent={CreateStatusBadgeFormatter}
            />
          )}
          <SortingState
            sorting={sorting}
            onSortingChange={(sorting: Array<Sorting>) => {
              setSorting(sorting)
            }}
          />
          <SearchState defaultValue='' />
          <IntegratedFiltering />
          <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
          <PagingState
            defaultCurrentPage={0}
            defaultPageSize={getPageSize(settings.pageSizeDevices)}
          />
          <IntegratedPaging />
          <Toolbar />
          <SearchPanel inputComponent={SearchPanelInputComponent} />
          <Table tableComponent={TableComponent} rowComponent={TableRowComponent} />
          <TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={(nextColumnWidths: Array<TableColumnWidthInfo>) =>
              setColumnWidths([...nextColumnWidths])
            }
            resizingMode='nextColumn'
          />
          <TableHeaderRow showSortingControls />
          <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />

          <Template name='root'>
            <TemplateConnector>
              {() => {
                return (
                  <>
                    <TemplatePlaceholder />
                    <div className='d-flex justify-content-end'>
                      <small className='mr-2'>
                        {!showOverlay ? (
                          <span>
                            Total {deviceCount} {deviceCount === 1 ? 'device' : 'devices'}
                          </span>
                        ) : (
                          ''
                        )}
                      </small>
                    </div>
                  </>
                )
              }}
            </TemplateConnector>
          </Template>
        </Grid>

        <DeleteModal
          url={apiUrls.devices}
          showModal={showModal}
          setShowModal={setShowModal}
          deleteEntryId={deleteEntryId}
          setDeleteEntryId={setDeleteEntryId}
          entityName='Device'
          getRows={getRows}
          setRows={setRows}
        />
      </>
    </Layout>
  )
}

export default Devices
